// Apply a persisted preference to the existing document. Keeping its nodes in
// place preserves open dialogs, typed amounts, pagination and scroll position.
let initialized = false;

function applyTheme(theme) {
  document.documentElement.dataset.theme = theme;

  document.querySelectorAll('[data-ui-theme-toggle]').forEach(toggle => {
    toggle.setAttribute('aria-label', theme === 'light' ? toggle.dataset.themeDarkLabel : toggle.dataset.themeLightLabel);
    toggle.querySelector('[data-theme-switch-track]').className = `button-${theme}`;
    toggle.querySelector('[data-theme-switch-knob]').className = theme === 'light' ? 'button-light-sun' : 'button-dark-moon';
  });
  document.querySelectorAll('[data-theme-src-light][data-theme-src-dark]').forEach(image => {
    image.src = theme === 'light' ? image.dataset.themeSrcLight : image.dataset.themeSrcDark;
  });
  document.querySelectorAll('[data-theme-class-light][data-theme-class-dark]').forEach(element => {
    element.classList.remove(element.dataset.themeClassLight, element.dataset.themeClassDark);
    element.classList.add(theme === 'light' ? element.dataset.themeClassLight : element.dataset.themeClassDark);
  });
}

export default function readyUiTheme() {
  if (initialized) return;
  initialized = true;
  let activeRequest = null;
  let recoveringSession = false;
  const enableToggles = () => {
    document.querySelectorAll('[data-ui-theme-toggle]').forEach(control => {
      control.removeAttribute('aria-disabled');
      control.removeAttribute('aria-busy');
    });
  };
  const resetRequests = () => {
    const previous = activeRequest;
    // Invalidate before abort: a delivered response or body can still settle
    // after this document is restored, including after a newer click.
    activeRequest = null;
    recoveringSession = false;
    if (previous) {
      window.clearTimeout(previous.timeout);
      previous.controller.abort();
    }
    enableToggles();
  };
  window.addEventListener('pagehide', resetRequests);

  // History can restore old HTML without restoring a live document. The
  // preference cookie survives both that path and bfcache restores. The layout
  // opts in explicitly; embedded/marketing layouts keep their forced theme.
  const restoreTheme = () => {
    if (document.documentElement.dataset.uiThemePreference !== 'true') return;
    const theme = document.cookie.split(';').map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith('ui_theme='))?.slice('ui_theme='.length);
    if (['dark', 'light'].includes(theme)) applyTheme(theme);
  };
  restoreTheme();
  window.addEventListener('pageshow', event => {
    if (event.persisted) resetRequests();
    restoreTheme();
  });

  // Capture before rails-ujs handles the link's regular POST fallback. The
  // delegated listener also survives Vue mounting the dashboard contents.
  document.addEventListener('click', async event => {
    const toggle = event.target.closest('[data-ui-theme-toggle]');
    if (!toggle) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (activeRequest || recoveringSession) return;

    const toggles = document.querySelectorAll('[data-ui-theme-toggle]');
    const errors = document.querySelectorAll('[data-ui-theme-error]');
    toggles.forEach(control => {
      control.setAttribute('aria-disabled', 'true');
      control.setAttribute('aria-busy', 'true');
    });
    errors.forEach(message => { message.hidden = true; });
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    const pending = { controller, timeout };
    activeRequest = pending;
    const theme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';

    try {
      const response = await fetch(toggle.href, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({ theme }),
        signal: controller.signal,
      });
      if (activeRequest !== pending) return;
      if (response.status === 401 || response.redirected) {
        recoveringSession = true;
        window.location.assign(toggle.dataset.themeLoginUrl || '/users/sign_in');
        return;
      }
      const result = await response.json();
      if (activeRequest !== pending) return;
      if (response.status === 422 && result.error_code === 'invalid_csrf_token') {
        recoveringSession = true;
        window.location.reload();
        return;
      }
      if (!response.ok) throw new Error('Theme preference was not saved');
      if (!['dark', 'light'].includes(result.theme)) throw new Error('Invalid theme preference');
      applyTheme(result.theme);
    } catch (_) {
      if (activeRequest !== pending) return;
      // Keep the current appearance and form contents; another click safely
      // retries the same desired value, even if the first response was lost.
      errors.forEach(message => { message.hidden = false; });
    } finally {
      window.clearTimeout(timeout);
      if (activeRequest === pending) {
        activeRequest = null;
        enableToggles();
      }
    }
  }, true);

  document.addEventListener('keydown', event => {
    if (![' ', 'Enter'].includes(event.key) || !event.target.matches('[data-ui-theme-toggle]')) return;
    // Cancel the anchor's native Enter click too: Firefox/WebKit repeat it
    // after each completed request while the key is held down.
    event.preventDefault();
    if (event.repeat) return;
    event.target.click();
  });
}
